import { convexQuery } from '@convex-dev/react-query'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useTable } from '@tanstack/react-table'
import type { FunctionReturnType } from 'convex/server'
import { IconChatBotOutline18 } from 'nucleo-ui-outline-18'
import { useMemo, useState } from 'react'
import { AssistantTools } from '~/components/admin/assistant-tools'
import { AssistantUsage } from '~/components/admin/assistant-usage'
import { Button } from '~/components/ui/button'
import type { DataTableFeatures } from '~/components/ui/data-table'
import { DataTable, dataTableFeatures, dataTableSortOptions } from '~/components/ui/data-table'
import { EmptyState } from '~/components/ui/empty-state'
import { PageHeader } from '~/components/ui/page-header'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Spinner } from '~/components/ui/spinner'
import { useGrowingPage } from '~/hooks/use-growing-page'
import { formatDuration, formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'
import { formatMicroUsd } from '~convex/lib/aiCost'

export const Route = createFileRoute('/admin/assistant/')({
  component: AdminWhatsApp,
  head: () => ({ meta: [{ title: titleFor(msg`WhatsApp`) }] }),
})

const PAGE_SIZE = 100
/** Grows the turn list in place; the server caps it well above this. */
const TURN_STEP = 50
/** Base UI selects cannot hold `undefined`, so "no filter" needs a value. */
const ALL = '__all__'

type Overview = FunctionReturnType<typeof api.adminAssistant.overview>
type LinkRow = Overview['links'][number]
type TurnRow = FunctionReturnType<typeof api.adminAssistant.turns>['rows'][number]
type MessageRow = FunctionReturnType<typeof api.adminAssistant.messages>['page'][number]

const EMPTY_MESSAGES: MessageRow[] = []
const EMPTY_TURNS: TurnRow[] = []

const LINK_STATUS_LABEL: Record<LinkRow['status'], MessageDescriptor> = {
  active: msg`Active`,
  pending: msg`Pending`,
  revoked: msg`Revoked`,
}

const LINK_STATUS_DOT: Record<LinkRow['status'], string> = {
  active: 'bg-emerald-500',
  pending: 'bg-amber-500',
  revoked: 'bg-muted-foreground/40',
}

const TURN_STATE_LABEL: Record<TurnRow['state'], MessageDescriptor> = {
  awaiting_reply: msg`Awaiting reply`,
  collecting: msg`Collecting`,
  done: msg`Done`,
  held: msg`Held`,
  processing: msg`Processing`,
  superseded: msg`Superseded`,
}

const TURN_STATE_ORDER: TurnRow['state'][] = [
  'collecting',
  'held',
  'processing',
  'awaiting_reply',
  'done',
  // Last because it is the one worth filtering FOR rather than scanning past:
  // a run of these is the debounce window being too long for how this person
  // types, which is a number nobody can see any other way (#73).
  'superseded',
]

const VERDICT_LABEL: Record<NonNullable<TurnRow['verdict']>, MessageDescriptor> = {
  flush: msg`Flush`,
  hold: msg`Hold`,
  more_coming: msg`More coming`,
  neutral: msg`Neutral`,
  respond_now: msg`Respond now`,
}

const VERDICT_ORDER: NonNullable<TurnRow['verdict']>[] = [
  'respond_now',
  'more_coming',
  'neutral',
  'flush',
  'hold',
]

const KIND_LABEL: Record<MessageRow['kind'], MessageDescriptor> = {
  audio: msg`Audio`,
  contacts: msg`Contact card`,
  document: msg`Document`,
  image: msg`Image`,
  interactive: msg`Button reply`,
  location: msg`Location`,
  reaction: msg`Reaction`,
  sticker: msg`Sticker`,
  text: msg`Text`,
  unsupported: msg`Unsupported`,
  video: msg`Video`,
}

function AdminWhatsApp() {
  const { t } = useLingui()
  const { data: overview } = useQuery(convexQuery(api.adminAssistant.overview, {}))
  const [linkFilter, setLinkFilter] = useState<Id<'waLinks'> | undefined>(undefined)

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        meta={t`O Assistente — linked numbers, message traffic, and delivery health`}
        title={t`WhatsApp`}
      />
      <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-5 pb-6">
        <AssistantUsage />
        <AssistantTools />
        {overview === undefined ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <>
            <LinksSection
              activeFilter={linkFilter}
              links={overview.links}
              onFilter={setLinkFilter}
            />
            <TurnsSection linkId={linkFilter} links={overview.links} />
          </>
        )}
        <MessagesSection linkId={linkFilter} />
      </div>
    </div>
  )
}

/**
 * One row per linked number. Clicking a row narrows the message feed below
 * to that chat; clicking again clears the filter.
 */
const LinksSection = ({
  links,
  activeFilter,
  onFilter,
}: {
  links: LinkRow[]
  activeFilter: Id<'waLinks'> | undefined
  onFilter: (linkId: Id<'waLinks'> | undefined) => void
}) => {
  const { t, i18n } = useLingui()

  return (
    <section>
      <h2 className="pb-3 font-medium text-muted-foreground text-sm">
        <Trans>Linked numbers</Trans>
      </h2>
      {links.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          <Trans>No number has messaged the Assistant yet.</Trans>
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-card">
          {links.map((link) => (
            <li key={link._id}>
              <button
                className={cn(
                  'flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent/50',
                  activeFilter === link._id && 'bg-accent'
                )}
                onClick={() => onFilter(activeFilter === link._id ? undefined : link._id)}
                type="button"
              >
                <span
                  className={cn('size-2 shrink-0 rounded-full', LINK_STATUS_DOT[link.status])}
                />
                <span className="font-medium tabular-nums">{link.phoneE164}</span>
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                  {i18n._(LINK_STATUS_LABEL[link.status])}
                </span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {link.userName !== null && link.workspaceName !== null ? (
                    <Trans>
                      {link.userName} · {link.workspaceName}
                    </Trans>
                  ) : (
                    <Trans>Not claimed by an account</Trans>
                  )}
                </span>
                <span className="shrink-0 text-muted-foreground text-xs tabular-nums">
                  {link.messageCountCapped
                    ? t`${link.messageCount}+ messages`
                    : t`${link.messageCount} messages`}
                </span>
                <span className="shrink-0 text-muted-foreground text-xs">
                  {link.lastMessageAt === null ? '—' : formatRelativeTime(link.lastMessageAt, i18n)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export const TurnStatePill = ({ state }: { state: TurnRow['state'] }) => {
  const { i18n } = useLingui()
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full px-1.5 py-0.5 text-xs',
        state === 'collecting' && 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
        state === 'processing' && 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
        state === 'held' && 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
        state === 'awaiting_reply' && 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
        state === 'done' && 'bg-muted text-muted-foreground',
        // Not red: being superseded is the engine working, not failing. It
        // reads as a muted `done` with just enough colour to be countable.
        state === 'superseded' && 'bg-muted text-muted-foreground/70 line-through'
      )}
    >
      {i18n._(TURN_STATE_LABEL[state])}
    </span>
  )
}

type TurnFilters = {
  onlyErrors: boolean
  state: TurnRow['state'] | undefined
  userId: Id<'users'> | undefined
  verdict: NonNullable<TurnRow['verdict']> | undefined
}

/**
 * The filter bar. Every option list is built from the same label source the
 * rows render, and each `Select` carries an `items` map — without it the
 * trigger shows the raw enum value, which reads as a translation bug.
 */
const TurnFilterBar = ({
  filters,
  links,
  onChange,
}: {
  filters: TurnFilters
  links: LinkRow[]
  onChange: (next: TurnFilters) => void
}) => {
  const { t, i18n } = useLingui()

  // The accounts that hold a link are exactly the accounts that can own a
  // turn, so the link list is the option list — no extra query.
  const users = useMemo(() => {
    const seen = new Map<Id<'users'>, string>()
    for (const link of links) {
      if (link.userId !== null && !seen.has(link.userId)) {
        seen.set(link.userId, link.userName ?? link.phoneE164)
      }
    }
    return [...seen].map(([id, name]) => ({ id, name }))
  }, [links])

  const userItems = useMemo(() => {
    const items: Record<string, string> = { [ALL]: t`All users` }
    for (const user of users) {
      items[user.id] = user.name
    }
    return items
  }, [users, t])

  const stateItems = useMemo(() => {
    const items: Record<string, string> = { [ALL]: t`All states` }
    for (const state of TURN_STATE_ORDER) {
      items[state] = i18n._(TURN_STATE_LABEL[state])
    }
    return items
  }, [i18n, t])

  const verdictItems = useMemo(() => {
    const items: Record<string, string> = { [ALL]: t`All verdicts` }
    for (const verdict of VERDICT_ORDER) {
      items[verdict] = i18n._(VERDICT_LABEL[verdict])
    }
    return items
  }, [i18n, t])

  return (
    <div className="flex flex-wrap items-center gap-2 pb-3">
      <Select
        items={userItems}
        onValueChange={(value) =>
          onChange({
            ...filters,
            userId: value === ALL || value === null ? undefined : (value as Id<'users'>),
          })
        }
        value={filters.userId ?? ALL}
      >
        <SelectTrigger aria-label={t`User`} className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t`All users`}</SelectItem>
          {users.map((user) => (
            <SelectItem key={user.id} value={user.id}>
              {user.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        items={stateItems}
        onValueChange={(value) =>
          onChange({
            ...filters,
            state:
              value === ALL || value === null
                ? undefined
                : (value as NonNullable<typeof filters.state>),
          })
        }
        value={filters.state ?? ALL}
      >
        <SelectTrigger aria-label={t`State`} className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t`All states`}</SelectItem>
          {TURN_STATE_ORDER.map((state) => (
            <SelectItem key={state} value={state}>
              {i18n._(TURN_STATE_LABEL[state])}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        items={verdictItems}
        onValueChange={(value) =>
          onChange({
            ...filters,
            verdict:
              value === ALL || value === null
                ? undefined
                : (value as NonNullable<typeof filters.verdict>),
          })
        }
        value={filters.verdict ?? ALL}
      >
        {/* Wide enough for "Todos os veredictos" — pt-BR runs longer than the
            English, and a clipped trailing letter reads as a typo. */}
        <SelectTrigger aria-label={t`Classifier verdict`} className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t`All verdicts`}</SelectItem>
          {VERDICT_ORDER.map((verdict) => (
            <SelectItem key={verdict} value={verdict}>
              {i18n._(VERDICT_LABEL[verdict])}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        onClick={() => onChange({ ...filters, onlyErrors: !filters.onlyErrors })}
        size="sm"
        type="button"
        variant={filters.onlyErrors ? 'default' : 'outline'}
      >
        <Trans>Errors only</Trans>
      </Button>

      {filters.onlyErrors ||
      filters.state !== undefined ||
      filters.userId !== undefined ||
      filters.verdict !== undefined ? (
        <Button
          onClick={() =>
            onChange({ onlyErrors: false, state: undefined, userId: undefined, verdict: undefined })
          }
          size="sm"
          type="button"
          variant="ghost"
        >
          <Trans>Clear</Trans>
        </Button>
      ) : null}
    </div>
  )
}

const TurnsSection = ({
  linkId,
  links,
}: {
  linkId: Id<'waLinks'> | undefined
  links: LinkRow[]
}) => {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const [limit, setLimit] = useState(TURN_STEP)
  const [filters, setFilters] = useState<TurnFilters>({
    onlyErrors: false,
    state: undefined,
    userId: undefined,
    verdict: undefined,
  })

  const { data, isPending } = useQuery(
    convexQuery(api.adminAssistant.turns, {
      limit,
      linkId,
      onlyErrors: filters.onlyErrors || undefined,
      state: filters.state,
      userId: filters.userId,
      verdict: filters.verdict,
    })
  )

  const columns = useMemo<ColumnDef<DataTableFeatures, TurnRow>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        cell: ({ row }) => formatRelativeTime(row.original.createdAt, i18n),
        header: () => t`Started`,
        meta: { className: 'w-0 whitespace-nowrap text-muted-foreground' },
        sortDescFirst: true,
      },
      {
        cell: ({ row }) => <TurnStatePill state={row.original.state} />,
        enableSorting: false,
        header: () => t`State`,
        id: 'state',
        meta: { className: 'w-0' },
      },
      {
        cell: ({ row }) =>
          row.original.verdict === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            i18n._(VERDICT_LABEL[row.original.verdict])
          ),
        enableSorting: false,
        header: () => t`Verdict`,
        id: 'verdict',
        meta: { className: 'w-0 whitespace-nowrap text-muted-foreground' },
      },
      {
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate">{row.original.userName ?? t`Unclaimed`}</span>
            <span className="truncate text-muted-foreground text-xs tabular-nums">
              {row.original.phoneE164 ?? '—'}
              {row.original.workspaceName === null ? '' : ` · ${row.original.workspaceName}`}
            </span>
          </div>
        ),
        enableSorting: false,
        header: () => t`Account`,
        id: 'account',
        meta: { className: 'max-w-0 w-full' },
      },
      {
        accessorKey: 'messageCount',
        header: () => t`Msgs`,
        meta: { className: 'w-0 text-right tabular-nums text-muted-foreground' },
      },
      {
        accessorKey: 'eventCount',
        cell: ({ row }) => (
          <span className={cn(row.original.errorCount > 0 && 'text-red-600 dark:text-red-400')}>
            {row.original.eventCount}
            {row.original.errorCount > 0 ? ` (${row.original.errorCount}!)` : ''}
          </span>
        ),
        header: () => t`Steps`,
        meta: { className: 'w-0 text-right tabular-nums text-muted-foreground' },
      },
      {
        accessorKey: 'aiCalls',
        cell: ({ row }) =>
          row.original.aiCalls === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            `${row.original.aiCalls} · ${formatMicroUsd(row.original.costMicroUsd)}`
          ),
        header: () => t`AI`,
        meta: { className: 'w-0 whitespace-nowrap text-right tabular-nums text-muted-foreground' },
      },
      {
        accessorKey: 'billableMessages',
        // Everything user-initiated is free, so any number here is a signal
        // that something left the 24h service window.
        cell: ({ row }) =>
          row.original.billableMessages === 0 ? (
            <span className="text-muted-foreground">
              <Trans>free</Trans>
            </span>
          ) : (
            <span className="font-medium text-amber-600 dark:text-amber-400">
              {row.original.billableMessages}
            </span>
          ),
        header: () => t`WhatsApp`,
        meta: { className: 'w-0 whitespace-nowrap text-right tabular-nums' },
      },
      {
        accessorKey: 'elapsedMs',
        cell: ({ row }) =>
          row.original.elapsedMs === null ? '—' : formatDuration(row.original.elapsedMs),
        header: () => t`Elapsed`,
        meta: { className: 'w-0 whitespace-nowrap text-right tabular-nums text-muted-foreground' },
      },
    ],
    [t, i18n]
  )

  const table = useTable({
    ...dataTableSortOptions,
    columns,
    data: data?.rows ?? EMPTY_TURNS,
    features: dataTableFeatures,
  })

  return (
    <section>
      <h2 className="pb-3 font-medium text-muted-foreground text-sm">
        {linkId === undefined ? <Trans>Turns</Trans> : <Trans>Turns for one number</Trans>}
      </h2>
      <TurnFilterBar filters={filters} links={links} onChange={setFilters} />
      <DataTable
        empty={
          <EmptyState
            className="animation-duration-30 animate-fade-up"
            description={t`Turns will appear here once a linked number sends a burst.`}
            icon={IconChatBotOutline18}
            title={t`No turns match`}
          />
        }
        isPending={isPending}
        onRowClick={(row) =>
          navigate({ params: { turnId: row._id }, to: '/admin/assistant/$turnId' })
        }
        table={table}
      />
      {data?.hasMore === true ? (
        <div className="flex justify-center py-3">
          <Button
            onClick={() => setLimit((current) => current + TURN_STEP)}
            size="sm"
            type="button"
            variant="outline"
          >
            <Trans>Load more</Trans>
          </Button>
        </div>
      ) : null}
    </section>
  )
}

/** Delivery ticks, WhatsApp-style: ✓ sent, ✓✓ delivered, blue ✓✓ read. */
const DeliveryTicks = ({ row }: { row: MessageRow }) => {
  const { t } = useLingui()
  if (row.direction === 'inbound') {
    return null
  }
  if (row.deliveryStatus === 'failed') {
    return (
      <span className="text-red-600 text-xs dark:text-red-400">
        {row.errorCode === null ? t`Failed` : t`Failed (${row.errorCode})`}
      </span>
    )
  }
  if (row.deliveryStatus === null) {
    return <span className="text-muted-foreground text-xs">…</span>
  }
  return (
    <span
      className={cn(
        'text-xs tabular-nums',
        row.deliveryStatus === 'read' ? 'text-blue-500' : 'text-muted-foreground'
      )}
    >
      {row.deliveryStatus === 'sent' ? '✓' : '✓✓'}
    </span>
  )
}

const MessagesSection = ({ linkId }: { linkId: Id<'waLinks'> | undefined }) => {
  const { t, i18n } = useLingui()
  const { paginationOpts, loadMore } = useGrowingPage(PAGE_SIZE)
  const { data, isPending } = useQuery(
    convexQuery(api.adminAssistant.messages, { linkId, paginationOpts })
  )

  const columns = useMemo<ColumnDef<DataTableFeatures, MessageRow>[]>(
    () => [
      {
        accessorKey: 'timestamp',
        cell: ({ row }) => formatRelativeTime(row.original.timestamp, i18n),
        header: () => t`When`,
        meta: { className: 'w-0 whitespace-nowrap text-muted-foreground' },
        sortDescFirst: true,
      },
      {
        cell: ({ row }) =>
          row.original.direction === 'inbound' ? (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
              <Trans>In</Trans>
            </span>
          ) : (
            <span className="rounded-full bg-foreground/90 px-1.5 py-0.5 text-background text-xs">
              <Trans>Out</Trans>
            </span>
          ),
        enableSorting: false,
        header: () => t`Direction`,
        id: 'direction',
        meta: { className: 'w-0 whitespace-nowrap' },
      },
      {
        accessorKey: 'phoneE164',
        cell: ({ row }) => row.original.phoneE164 ?? '—',
        header: () => t`Number`,
        meta: { className: 'hidden w-0 whitespace-nowrap text-muted-foreground md:table-cell' },
      },
      {
        cell: ({ row }) => (
          <div className="flex min-w-0 items-baseline gap-2">
            {row.original.kind === 'text' ? null : (
              <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                {i18n._(KIND_LABEL[row.original.kind])}
              </span>
            )}
            {row.original.forwarded ? (
              <span className="shrink-0 text-muted-foreground text-xs italic">
                <Trans>forwarded</Trans>
              </span>
            ) : null}
            <span className="truncate">
              {row.original.text ?? row.original.transcript ?? (
                <span className="text-muted-foreground">—</span>
              )}
            </span>
          </div>
        ),
        enableSorting: false,
        header: () => t`Message`,
        id: 'content',
        meta: { className: 'max-w-0 w-full' },
      },
      {
        cell: ({ row }) => <DeliveryTicks row={row.original} />,
        enableSorting: false,
        header: () => t`Status`,
        id: 'status',
        meta: { className: 'w-0 whitespace-nowrap text-right' },
      },
    ],
    [t, i18n]
  )

  const table = useTable({
    ...dataTableSortOptions,
    columns,
    data: data?.page ?? EMPTY_MESSAGES,
    features: dataTableFeatures,
  })

  return (
    <section>
      <h2 className="pb-3 font-medium text-muted-foreground text-sm">
        {linkId === undefined ? <Trans>Messages</Trans> : <Trans>Messages for one number</Trans>}
      </h2>
      <DataTable
        empty={
          <EmptyState
            className="animation-duration-30 animate-fade-up"
            description={t`Inbound and outbound WhatsApp traffic will appear here.`}
            icon={IconChatBotOutline18}
            title={t`No messages yet`}
          />
        }
        isPending={isPending}
        table={table}
      />
      {data !== undefined && !data.isDone ? (
        <div className="flex justify-center py-3">
          <Button onClick={loadMore} size="sm" type="button" variant="outline">
            <Trans>Load more</Trans>
          </Button>
        </div>
      ) : null}
    </section>
  )
}
